import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigate,
  Outlet,
  Navigate,
  useParams,
} from 'react-router-dom'
import './App.css'
import ProtectedRoute from './components/ProtectedRoute'
import { AuthProvider } from './contexts/AuthContext'
import { EmployeeDirectoryProvider } from './contexts/EmployeeDirectoryContext'
import { UserPermissionsProvider } from './contexts/UserPermissionsContext'
import { ProfilePictureProvider } from './contexts/ProfilePictureContext'
import { Suspense, useEffect } from 'react'
import { lazyWithRetry } from './utils/lazyWithRetry'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import Layout from './components/Layout'
import AccessGate from './components/AccessGate'
import HandbookAccessGate from './components/handbook/HandbookAccessGate'
import HomeRouteModal from './features/home-preview/HomeRouteModal'
import { HubTopBar } from './components/HubTopBar'
import type { AccessFlags } from './contexts/UserPermissionsContext'
import { policyReaderPath } from './common/policyReader'

const InterviewsPage = lazyWithRetry(() => import('./features/interviews/InterviewsPage'))
const InterviewPage = lazyWithRetry(() => import('./features/interviews/InterviewPage'))
const InterviewQuestionTemplatesPage = lazyWithRetry(() => import('./features/interviews/InterviewQuestionTemplatesPage'))
const UniformsPage = lazyWithRetry(() => import('./features/uniforms/UniformsPage'))

const gate = (flag: keyof AccessFlags, el: React.ReactNode) => (
  <AccessGate flag={flag}>{el}</AccessGate>
)

// Old "read this policy" links open the policy reader over the Policies page.
function LegacyPolicyReadRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate replace to={/^\d+$/.test(id || '') ? policyReaderPath(Number(id)) : '/policies'} />
}

function LegacyProfileRedirect({ bankDetails = false }: { bankDetails?: boolean }) {
  const location = useLocation()
  const search = new URLSearchParams(location.search)
  if (bankDetails) search.set('tab', 'bank')
  return <Navigate replace to={{
    pathname: '/my-profile',
    search: search.toString(),
    hash: location.hash,
  }} />
}

// Lazy load pages with auto-retry on chunk load failure (handles deployments gracefully)
const Home = lazyWithRetry(() => import('./pages/Home'))
const DoorPage = lazyWithRetry(() => import('./pages/DoorPage'))
const DoorSetupPage = lazyWithRetry(() => import('./pages/DoorSetupPage'))
const HomeNew = lazyWithRetry(() => import('./pages/HomeNew'))
const OnboardingActionPage = lazyWithRetry(() => import('./features/home-preview/OnboardingActionModal').then(module => ({ default: module.OnboardingActionPage })))
const ShutdownSkeletonCrewPage = lazyWithRetry(() => import('./features/leave/components/ShutdownSkeletonCrewPage'))
const ParraSeatingPlanPage = lazyWithRetry(() => import('./features/parra-seating/ParraSeatingPlanPage'))
const HomeLeaveRequest = lazyWithRetry(() => import('./features/home-preview/HomeLeavePanels').then(module => ({ default: module.HomeLeaveRequest })))
const HomeLeaveDetail = lazyWithRetry(() => import('./features/home-preview/HomeLeavePanels').then(module => ({ default: module.HomeLeaveDetail })))
const HomeLeaveContent = lazyWithRetry(() => import('./features/leave/components/LeavePage').then(module => ({ default: () => <module.LeavePage embedded /> })))
const HomeITContent = lazyWithRetry(() => import('./pages/ITTicketPage').then(module => ({ default: () => <module.default embedded /> })))
const MemoPage = lazyWithRetry(() => import('./pages/MemoPage'))
const OrgChartPage = lazyWithRetry(() => import('./pages/OrgChartPage'))
const MyTeamPage = lazyWithRetry(() => import('./pages/MyTeamPage'))
const DirectoryPage = lazyWithRetry(() => import('./pages/DirectoryPage'))
const KPIPage = lazyWithRetry(() => import('./pages/KPIPage'))
const StaffActionsDataPage = lazyWithRetry(() => import('./pages/StaffActionsDataPage'))
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage'))
const DqsWorkspacePage = lazyWithRetry(() => import('./pages/DqsWorkspacePage'))
const SharePointAccessPage = lazyWithRetry(() => import('./pages/SharePointAccessPage'))
const NotFound = lazyWithRetry(() => import('./pages/NotFound'))
const LeavePage = lazyWithRetry(() =>
  import('./features/leave/components/LeavePage').then((m) => ({ default: m.LeavePage }))
)
const LeaveAdminPage = lazyWithRetry(() =>
  import('./features/leave/components/LeaveAdminPage').then((m) => ({ default: m.LeaveAdminPage }))
)
// LeaveDetailPage removed - now handled by LeavePage with modal
const LeaveAdminSettingsPage = lazyWithRetry(() => import('./pages/LeaveAdminSettingsPage'))
const MeetingMinutesPage = lazyWithRetry(() => import('./pages/MeetingMinutesPage'))
const MeetingDetailPage = lazyWithRetry(() => import('./pages/MeetingDetailPage'))
const MyTasksPage = lazyWithRetry(() => import('./pages/MyTasksPage'))
const MinutesAdminPage = lazyWithRetry(() => import('./pages/MinutesAdminPage'))
const SuperstarPage = lazyWithRetry(() => import('./pages/SuperstarPage'))
const EofyDinnerPage = lazyWithRetry(() => import('./pages/EofyDinnerPage'))
const PolicyReviewPage = lazyWithRetry(() => import('./pages/PolicyReviewPage'))
const McpConnectPage = lazyWithRetry(() => import('./pages/McpConnectPage'))
const EofyDinnerAdminPage = lazyWithRetry(() => import('./pages/EofyDinnerAdminPage'))
const HaircutQueuePage = lazyWithRetry(() => import('./pages/HaircutQueuePage'))
const HaircutAdminPage = lazyWithRetry(() => import('./pages/HaircutAdminPage'))
const MassageQueuePage = lazyWithRetry(() => import('./pages/MassageQueuePage'))
const MassageAdminPage = lazyWithRetry(() => import('./pages/MassageAdminPage'))
const ITTicketPage = lazyWithRetry(() => import('./pages/ITTicketPage'))
const MarketingSuggestionPage = lazyWithRetry(() => import('./pages/MarketingSuggestionPage'))
const EinsteinIdeasPage = lazyWithRetry(() => import('./pages/EinsteinIdeasPage'))
const DoorAccessHistory = lazyWithRetry(() => import('./features/system-development/components/DoorAccessHistory'))
const SystemDevelopmentLayout = lazyWithRetry(() => import('./pages/SystemDevelopmentLayout'))
const SDAssetRegister = lazyWithRetry(() =>
  import('./features/system-development/asset-register/AssetRegister')
)
const AssetReceiptsPage = lazyWithRetry(() =>
  import('./features/system-development/asset-register/AssetReceipts')
)
const SDOnboardingSection = lazyWithRetry(() =>
  import('./features/system-development/components/OnboardingSection').then((m) => ({
    default: m.OnboardingSection,
  }))
)
const SDHolidaysSection = lazyWithRetry(() =>
  import('./features/system-development/components/HolidaysSection').then((m) => ({
    default: m.HolidaysSection,
  }))
)
const SDPhotosSection = lazyWithRetry(() =>
  import('./features/system-development/components/PhotosSection').then((m) => ({
    default: m.PhotosSection,
  }))
)
const SDCompanyShutdownAdmin = lazyWithRetry(() =>
  import('./features/system-development/components/CompanyShutdownAdmin').then((m) => ({
    default: m.CompanyShutdownAdmin,
  }))
)
const SDITSupportStaffAdmin = lazyWithRetry(() =>
  import('./features/system-development/components/ITSupportStaffAdmin').then((m) => ({
    default: m.ITSupportStaffAdmin,
  }))
)
const SDSfApiUsagePage = lazyWithRetry(() =>
  import('./features/system-development/components/SfApiUsagePage').then((m) => ({
    default: m.SfApiUsagePage,
  }))
)
const SDStatusTab = lazyWithRetry(() =>
  import('./features/system-development/components/StatusTab').then((m) => ({
    default: m.StatusTab,
  }))
)
const SDDuoBaseRequestsTab = lazyWithRetry(() =>
  import('./features/system-development/components/DuoBaseRequestsTab').then((m) => ({
    default: m.DuoBaseRequestsTab,
  }))
)
const SDDuoBaseMetricsTab = lazyWithRetry(() =>
  import('./features/system-development/components/DuoBaseMetricsTab').then((m) => ({
    default: m.DuoBaseMetricsTab,
  }))
)
const SDPromptsTab = lazyWithRetry(() =>
  import('./features/system-development/components/PromptsTab').then((m) => ({
    default: m.PromptsTab,
  }))
)
const SDMeetingSharingTab = lazyWithRetry(() =>
  import('./features/system-development/components/MeetingSharingTab').then((m) => ({
    default: m.MeetingSharingTab,
  }))
)
const SDStaffActionsTab = lazyWithRetry(() =>
  import('./features/system-development/components/StaffActionsTab').then((m) => ({
    default: m.StaffActionsTab,
  }))
)
const SDNotesAccessTab = lazyWithRetry(() =>
  import('./features/system-development/components/NotesAccessTab').then((m) => ({
    default: m.NotesAccessTab,
  }))
)
const SDTeamsLeaveMonitorTab = lazyWithRetry(() =>
  import('./features/system-development/components/TeamsLeaveMonitorTab').then((m) => ({
    default: m.TeamsLeaveMonitorTab,
  }))
)
const SDAppElementsTab = lazyWithRetry(() =>
  import('./features/system-development/components/AppElementsTab').then((m) => ({
    default: m.AppElementsTab,
  }))
)
const SDAppsTab = lazyWithRetry(() =>
  import('./features/system-development/components/AppsTab').then((m) => ({
    default: m.AppsTab,
  }))
)
const DuoServicesPage = lazyWithRetry(() => import('./pages/DuoServicesPage'))
const SDAppDetailPage = lazyWithRetry(() =>
  import('./features/system-development/components/AppDetailPage').then((m) => ({
    default: m.AppDetailPage,
  }))
)
const SDOfficeScreenTab = lazyWithRetry(() =>
  import('./features/system-development/components/OfficeScreenTab').then((m) => ({
    default: m.OfficeScreenTab,
  }))
)
const SDWiseVerificationTab = lazyWithRetry(() =>
  import('./features/system-development/components/WiseVerificationTab').then((m) => ({
    default: m.WiseVerificationTab,
  }))
)
const DevicesLayout = lazyWithRetry(
  () => import('./features/system-development/it-devices/DevicesLayout')
)
const DevicesHome = lazyWithRetry(() => import('./features/system-development/it-devices/HomePage'))
const DevicePage = lazyWithRetry(
  () => import('./features/system-development/it-devices/DevicePage')
)
const DevicesScan = lazyWithRetry(() => import('./features/system-development/it-devices/ScanPage'))
const DevicesLabels = lazyWithRetry(
  () => import('./features/system-development/it-devices/LabelsPage')
)
const DevicesSync = lazyWithRetry(() => import('./features/system-development/it-devices/SyncPage'))
const DevicesPeople = lazyWithRetry(() =>
  import('./features/system-development/it-devices/PeoplePages').then((m) => ({
    default: m.PeoplePage,
  }))
)
const DevicesPerson = lazyWithRetry(() =>
  import('./features/system-development/it-devices/PeoplePages').then((m) => ({
    default: m.PersonPage,
  }))
)
const MeetingRoomPage = lazyWithRetry(() => import('./pages/MeetingRoomPage'))
const RoomManagementPage = lazyWithRetry(() => import('./pages/RoomManagementPage'))
const BillsPage = lazyWithRetry(() => import('./pages/BillsPage'))
const BillsAdminPage = lazyWithRetry(() => import('./pages/BillsAdminPage'))
const CafePage = lazyWithRetry(() => import('./pages/CafePage'))
const CafeAdminDashboard = lazyWithRetry(() => import('./pages/CafeAdminDashboard'))
const CafeAllOrders = lazyWithRetry(() => import('./pages/CafeAllOrders'))
const CafeNotificationHistory = lazyWithRetry(() => import('./pages/CafeNotificationHistory'))
const MollyTeaPage = lazyWithRetry(() => import('./pages/MollyTeaPage'))
const MemoAdminPage = lazyWithRetry(() => import('./pages/MemoAdminPage'))
const DocumentsPage = lazyWithRetry(() => import('./pages/DocumentsPage'))
const DocumentEditPage = lazyWithRetry(() => import('./pages/DocumentEditPage'))
const DocumentSigningPage = lazyWithRetry(() => import('./pages/DocumentSigningPage'))
const PoliciesPage = lazyWithRetry(() => import('./pages/PoliciesPage'))
const DocumentViewPage = lazyWithRetry(() => import('./pages/DocumentViewPage'))
const BNIPage = lazyWithRetry(() =>
  import('./components/bni/BNIPage').then((m) => ({ default: m.BNIPage }))
)

// KPI Question List pages
const QuestionListsPage = lazyWithRetry(() => import('./pages/QuestionListsPage'))
const CreateQuestionListPage = lazyWithRetry(() => import('./pages/CreateQuestionListPage'))
const EditQuestionListPage = lazyWithRetry(() => import('./pages/EditQuestionListPage'))

// KPI Form pages
const FormsPage = lazyWithRetry(() => import('./pages/FormsPage'))
const EditFormPage = lazyWithRetry(() => import('./pages/EditFormPage'))

// KPI Archive page
const ArchivePage = lazyWithRetry(() => import('./pages/ArchivePage'))

// KPI Submissions page
const SubmissionsPage = lazyWithRetry(() => import('./pages/SubmissionsPage'))

// KPI Admin Management page
const AdminManagementPage = lazyWithRetry(() => import('./pages/AdminManagementPage'))

// KPI Migration page
const KPIMigrationPage = lazyWithRetry(() => import('./pages/KPIMigrationPage'))

// User KPI Form components (form filling and review)
const FormFillPage = lazyWithRetry(() =>
  import('./features/kpi-form-user/components/FormFillPage').then((m) => ({
    default: m.FormFillPage,
  }))
)
const FormReviewPage = lazyWithRetry(() =>
  import('./features/kpi-form-user/components/FormReviewPage').then((m) => ({
    default: m.FormReviewPage,
  }))
)
const CaseStudiesPage = lazyWithRetry(() => import('./pages/CaseStudiesPage'))
const CaseStudyDetailPage = lazyWithRetry(() => import('./pages/CaseStudyDetailPage'))
const WiseSetupPage = lazyWithRetry(() => import('./pages/WiseSetupPage'))
const BankDetailsMissingPage = lazyWithRetry(() => import('./pages/BankDetailsMissingPage'))
const MySuperDetailsPage = lazyWithRetry(() => import('./pages/MySuperDetailsPage'))
const MyProfilePage = lazyWithRetry(() => import('./pages/MyProfilePage'))
const WellbeingAllowancePage = lazyWithRetry(
  () => import('./pages/WellbeingAllowancePage')
)
const SecurityTrainingPage = lazyWithRetry(() => import('./pages/SecurityTrainingPage'))
const HandbookPage = lazyWithRetry(() => import('./pages/handbook/HandbookPage'))
const HandbookArticlePage = lazyWithRetry(() => import('./pages/handbook/HandbookArticlePage'))
const HandbookEditorPage = lazyWithRetry(() => import('./pages/handbook/HandbookEditorPage'))
const HandbookAdminPage = lazyWithRetry(() => import('./pages/handbook/HandbookAdminPage'))
const HandbookAssignmentsPage = lazyWithRetry(
  () => import('./pages/handbook/HandbookAssignmentsPage')
)
const HandbookAssignmentPage = lazyWithRetry(
  () => import('./pages/handbook/HandbookAssignmentPage')
)

// Create a wrapper component that handles the auth redirect logic
const AuthRedirectHandler = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (location.search.includes('authSuccess=true')) {
      const savedUrl = sessionStorage.getItem('redirectUrl')
      sessionStorage.removeItem('redirectUrl')

      if (savedUrl && savedUrl !== '/') {
        setTimeout(() => {
          navigate(savedUrl, { replace: true })
        }, 100)
      }
    }
  }, [location, navigate])

  return <>{children}</>
}

const queryClient = new QueryClient()

// Root wrapper with providers (Suspense lives in Layout to keep the top bar persistent)
const RootLayout = () => {
  return (
    <AuthProvider>
      <ProfilePictureProvider>
        <EmployeeDirectoryProvider>
          <UserPermissionsProvider>
            <AuthRedirectHandler>
              <Outlet />
            </AuthRedirectHandler>
          </UserPermissionsProvider>
        </EmployeeDirectoryProvider>
      </ProfilePictureProvider>
    </AuthProvider>
  )
}

/**
 * Router Configuration with Proper Layout Architecture
 *
 * Architecture:
 * - RootLayout: auth/employee-directory contexts + page transitions
 *   ├─ Layout: Header + Sidebar + Main content (ALL main pages)
 *   │  ├─ Home (/)
 *   │  ├─ Admin (/admin) - Team Apps, Company Services, Core Values, Companies tabs
 *   │  ├─ KPI (/kpi) - Dashboard page with My Assessments tabs
 *   │  ├─ KPI Question Lists (/kpi/questions, /kpi/questions/new, /kpi/questions/:id)
 *   │  ├─ KPI Forms (/kpi/forms, /kpi/forms/new, /kpi/forms/:id)
 *   │  ├─ My Assessments (/kpi/my-assessments/:id) - individual form filling
 *   │  ├─ Submissions (Admin) (/kpi/submissions, /kpi/submissions/:id)
 *   │  ├─ Archive (/kpi/archive)
 *   │  ├─ OrgChartPage (/org-chart)
 *   │  ├─ MemoPage (/memo)
 *   │  ├─ LeavePage (/leave)
 *   │  ├─ MinutesPage (/minutes)
 *   │  ├─ SuperstarPage (/superstar)
 *   │  └─ ITTicketPage (/it-tickets)
 *   └─ NotFound (*): Error page
 *
 * Layout features:
 * - Header: Persistent AppHeader with features (admin, notifications, core values)
 * - Sidebar: Collapsible navigation to all main pages
 * - Main: Content area with page transitions
 */
const router = createBrowserRouter(
  [
    { path: '/door', element: <AuthProvider><Suspense fallback={null}><DoorPage /></Suspense></AuthProvider> },
    { path: '/door/setup', element: <AuthProvider><Suspense fallback={null}><DoorSetupPage /></Suspense></AuthProvider> },
    {
      element: <RootLayout />,
      children: [
        {
          // Main app layout - top bar + content (protected)
          element: (
            <ProtectedRoute>
              <Layout showTopBar enableAdminOverride enableNotifications />
            </ProtectedRoute>
          ),
          children: [
            {
              path: '/',
              element: <HomeNew />,
              children: [
                { path: 'home-new', element: <Navigate to="/" replace /> },
                { path: 'home-new/shutdown-skeleton-crew', element: <Navigate to="/shutdown-skeleton-crew" replace /> },
                { path: 'home-new/leave', element: <HomeRouteModal key="leave" title="Leave"><HomeLeaveContent /></HomeRouteModal> },
                { path: 'home-new/leave/new', element: <HomeLeaveRequest /> },
                { path: 'home-new/leave/:id', element: <HomeLeaveDetail /> },
                { path: 'home-new/it-tickets', element: <HomeRouteModal key="it" title="IT help"><HomeITContent /></HomeRouteModal> },
              ],
            },
            {
              path: '/shutdown-skeleton-crew',
              element: <ShutdownSkeletonCrewPage />,
            },
            {
              path: '/parra-seating-plan',
              element: <ParraSeatingPlanPage />,
            },
            {
              path: '/admin',
              element: gate('adminPanel', <AdminPage />),
            },
            {
              path: '/dqs',
              element: gate('dqsAdmin', <DqsWorkspacePage />),
            },
            {
              path: '/sharepoint-access',
              element: gate('dqsAdmin', <SharePointAccessPage />),
            },
            {
              path: '/interviews',
              element: <InterviewsPage />,
            },
            {
              path: '/interviews/:id',
              element: <InterviewPage />,
            },
            {
              path: '/interviews/questions/templates',
              element: <InterviewQuestionTemplatesPage />,
            },
            {
              path: '/uniforms',
              element: <UniformsPage />,
            },
            {
              path: '/my-team',
              element: <MyTeamPage />,
            },
            {
              // Line-manager Schedule Adherence report (guards access in-page).
              path: '/staff_actions_data',
              element: <StaffActionsDataPage />,
            },
            {
              path: '/directory',
              element: gate('directory', <DirectoryPage />),
            },
            {
              path: '/kpi',
              element: <KPIPage />,
            },
            {
              path: '/kpi/admin',
              element: gate('kpiAdmin', <AdminManagementPage />),
            },
            {
              path: '/wise-setup',
              element: <WiseSetupPage />,
            },
            {
              // Temporary Philippines bank-details audit (access checked by the API).
              path: '/bank-details/missing',
              element: <BankDetailsMissingPage />,
            },
            {
              path: '/home-new/my-profile/bank-details',
              element: <LegacyProfileRedirect bankDetails />,
            },
            {
              path: '/home-new/my-profile',
              element: <LegacyProfileRedirect />,
            },
            {
              path: '/my-profile/bank-details',
              element: <LegacyProfileRedirect bankDetails />,
            },
            {
              path: '/my-super-details',
              element: <MySuperDetailsPage />,
            },
            {
              path: '/onboarding-actions/:actionId',
              element: <OnboardingActionPage />,
            },
            {
              path: '/my-profile',
              element: <MyProfilePage />,
            },
            {
              path: '/security-training',
              element: <SecurityTrainingPage />,
            },
            {
              path: '/bills',
              element: gate('bills', <BillsPage />),
            },
            {
              path: '/bills/new',
              element: gate('bills', <BillsPage />),
            },
            {
              path: '/bills/approval',
              element: gate('bills', <BillsPage />),
            },
            {
              path: '/bills/reconciliation',
              element: gate('bills', <BillsPage />),
            },
            {
              path: '/bills/reconciliation/:billId',
              element: gate('bills', <BillsPage />),
            },
            {
              path: '/bills/admin',
              element: gate('billsAdmin', <BillsAdminPage />),
            },
            {
              path: '/bills/view/:billId',
              element: gate('bills', <BillsPage />),
            },
            {
              path: '/org-chart',
              element: <OrgChartPage />,
            },
            {
              path: '/leave',
              element: <LeavePage />,
            },
            {
              path: '/leave/new',
              element: <LeavePage />,
            },
            {
              path: '/leave/admin',
              element: gate('leaveAdmin', <LeaveAdminPage />),
            },
            {
              path: '/leave/settings',
              element: gate('leaveAdmin', <LeaveAdminSettingsPage />),
            },
            {
              path: '/leave/:id',
              element: <LeavePage />,
            },
            // Meeting Minutes routes
            {
              path: '/minutes',
              element: <MeetingMinutesPage />,
            },
            {
              path: '/minutes/:id/task/:taskId',
              element: <MeetingDetailPage />,
            },
            {
              path: '/minutes/:id',
              element: <MeetingDetailPage />,
            },
            {
              path: '/minutes/admin',
              element: gate('minutesAdmin', <MinutesAdminPage />),
            },
            {
              path: '/superstar',
              element: <SuperstarPage />,
            },
            {
              path: '/eofy-dinner',
              element: <EofyDinnerPage />,
            },
            {
              path: '/policy-review',
              element: <PolicyReviewPage />,
            },
            {
              // Browser sign-in for Claude Code / Codex (Duo MCP), open to all staff.
              path: '/connect/mcp',
              element: <McpConnectPage />,
            },
            {
              path: '/eofy-dinner/admin',
              element: <EofyDinnerAdminPage />,
            },
            {
              path: '/haircuts',
              element: <HaircutQueuePage />,
            },
            {
              path: '/haircuts/admin',
              element: <HaircutAdminPage />,
            },
            {
              path: '/massages',
              element: <MassageQueuePage />,
            },
            {
              path: '/massages/admin',
              element: <MassageAdminPage />,
            },
            {
              path: '/it-tickets',
              element: <ITTicketPage />,
            },
            {
              path: '/marketing-suggestions',
              element: <MarketingSuggestionPage />,
            },
            {
              path: '/einstein-submission',
              element: <EinsteinIdeasPage />,
            },
            {
              path: '/asset-receipts',
              element: <AssetReceiptsPage />,
            },
            {
              path: '/system-development',
              element: gate('systemDev', <SystemDevelopmentLayout />),
              children: [
                { path: 'apps', element: <SDAppsTab /> },
                { path: 'duo-services', element: <DuoServicesPage /> },
                { path: 'apps/:key', element: <SDAppDetailPage /> },
                { path: 'app-elements', element: <SDAppElementsTab /> },
                { path: 'status', element: <SDStatusTab /> },
                { path: 'door-history', element: <DoorAccessHistory /> },
                { path: 'duo-base', element: <SDDuoBaseMetricsTab /> },
                { path: 'duo-base-requests', element: <SDDuoBaseRequestsTab /> },
                { path: 'sf-api', element: <SDSfApiUsagePage /> },
                { path: 'onboarding', element: <SDOnboardingSection /> },
                { path: 'admin', element: <SDITSupportStaffAdmin /> },
                { path: 'photos', element: <SDPhotosSection /> },
                { path: 'holidays', element: <SDHolidaysSection /> },
                { path: 'shutdowns', element: <SDCompanyShutdownAdmin /> },
                { path: 'leave-monitor', element: <SDTeamsLeaveMonitorTab /> },
                { path: 'asset-register', element: <SDAssetRegister /> },
                {
                  path: 'devices',
                  element: <DevicesLayout />,
                  children: [
                    { index: true, element: <DevicesHome /> },
                    { path: 'scan', element: <DevicesScan /> },
                    { path: 'people', element: <DevicesPeople /> },
                    { path: 'people/:id', element: <DevicesPerson /> },
                    { path: 'labels', element: <DevicesLabels /> },
                    { path: 'sync', element: <DevicesSync /> },
                    { path: ':tag', element: <DevicePage /> },
                  ],
                },
                { path: 'duo-mcp', element: <SDPromptsTab /> },
                { path: 'sharing', element: <SDMeetingSharingTab /> },
                { path: 'staff-actions', element: <SDStaffActionsTab /> },
                { path: 'notes-access', element: <SDNotesAccessTab /> },
                { path: 'office-screen', element: <SDOfficeScreenTab /> },
                { path: 'wise-verification', element: <SDWiseVerificationTab /> },
              ],
            },
            {
              path: '/duo-services',
              element: gate('duoServices', <DuoServicesPage />),
            },
            {
              // Retired feature: old bookmarks return to the dashboard.
              path: '/it-management/*',
              element: <Navigate to="/" replace />,
            },
            {
              path: '/meeting-room',
              element: <MeetingRoomPage />,
            },
            {
              path: '/meeting-room/admin',
              element: gate('roomAdmin', <RoomManagementPage />),
            },
            {
              path: '/cafe',
              element: gate('cafe', <CafePage />),
            },
            {
              path: '/cafe/history',
              element: gate('cafe', <CafePage />),
            },
            {
              path: '/cafe/orders',
              element: gate('cafeAdmin', <CafePage />),
            },
            {
              path: '/cafe/default',
              element: gate('cafe', <CafePage />),
            },
            {
              path: '/cafe/admin',
              element: gate('cafeAdmin', <CafePage />),
            },
            {
              path: '/molly-tea',
              element: <MollyTeaPage />,
            },
            // My Tasks routes
            {
              path: '/tasks/:taskId',
              element: <MyTasksPage />,
            },
            {
              path: '/tasks',
              element: <MyTasksPage />,
            },
            {
              path: '/my-tasks',
              element: <MyTasksPage />,
            },
            // KPI Routes - RESTful structure
            // Question Lists (Admin)
            {
              path: '/kpi/questions',
              element: gate('kpiAdmin', <QuestionListsPage />),
            },
            {
              path: '/kpi/questions/new',
              element: gate('kpiAdmin', <CreateQuestionListPage />),
            },
            {
              path: '/kpi/questions/:id',
              element: gate('kpiAdmin', <EditQuestionListPage />),
            },
            // Form Edit (Admin)
            {
              path: '/kpi/admin/form/:id',
              element: gate('kpiAdmin', <EditFormPage />),
            },
            // Form Fill (User Assessment)
            {
              path: '/kpi/:id',
              element: <FormFillPage />,
            },
            // Submissions (Admin + KPI Managers)
            {
              path: '/kpi/submissions',
              element: <SubmissionsPage />,
            },
            {
              path: '/kpi/submissions/:id',
              element: <FormReviewPage />,
            },
            // Archive (Admin)
            {
              path: '/kpi/archive',
              element: gate('kpiAdmin', <ArchivePage />),
            },
            // Wellbeing Allowance overview (offshore staff tiers) — access managed in-page
            {
              path: '/wellbeing-allowance',
              element: gate(
                'wellbeingAllowance',
                <WellbeingAllowancePage />
              ),
            },
            // Migration (Admin)
            {
              path: '/kpi/migration',
              element: gate('kpiAdmin', <KPIMigrationPage />),
            },
            {
              path: '/memo',
              element: <MemoPage />,
            },
            {
              path: '/memo/admin',
              element: gate('memoAdmin', <MemoAdminPage />),
            },
            {
              path: '/documents',
              element: <DocumentsPage />,
            },
            {
              path: '/documents/:id',
              element: <DocumentViewPage />,
            },
            {
              path: '/documents/:id/sign',
              element: <DocumentSigningPage />,
            },
            {
              path: '/documents/:id/edit',
              element: <DocumentEditPage />,
            },
            {
              path: '/policies',
              element: <PoliciesPage />,
            },
            {
              path: '/policies/:id/read',
              element: <LegacyPolicyReadRedirect />,
            },
            {
              path: '/bni',
              element: <BNIPage />,
            },
            {
              path: '/case-studies',
              element: <CaseStudiesPage />,
            },
            {
              path: '/case-studies/:id',
              element: <CaseStudyDetailPage />,
            },
            {
              path: '/handbook',
              element: <HandbookAccessGate />,
              children: [
                { index: true, element: <HandbookPage /> },
                { path: 'admin', element: <HandbookAdminPage /> },
                { path: 'editor/new', element: <HandbookEditorPage /> },
                { path: 'editor/:slug', element: <HandbookEditorPage /> },
                { path: 'assignments', element: <HandbookAssignmentsPage /> },
                { path: 'assignments/:id', element: <HandbookAssignmentPage /> },
                { path: ':slug', element: <HandbookArticlePage /> },
              ],
            },
            {
              path: '/preferences',
              element: <Home />,
            },
          ],
        },
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
)

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HubTopBar />
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
        }}
      />
      <Toaster position="top-right" richColors expand={true} gap={8} />
    </QueryClientProvider>
  )
}

export default App
